import { useEffect, useRef, useState } from 'react';
import {
  useCustomMutation,
  useInvalidate,
  useNotification,
  useTranslate,
} from '@refinedev/core';
import { Button, Checkbox, Flex, Modal, Spin, Typography, theme } from 'antd';
import type {
  PublicationDiffOut,
  ScalarDiffFieldOut,
} from '@/api/generated/schemas';
import { axiosInstance } from '@/api/client';
import { extractBackendMessage } from '@/utils/apiError';
import { ShowLink } from '@/components/common/ShowLink';
import { diffFieldLabel } from './diffFieldLabel';
import { willImportApply } from './diffFieldApply';
import { ImportDiffTable } from './ImportDiffTable';
import { SyncReviewResult } from './SyncReviewResult';
import type {
  AutoApplyGroup,
  SyncReviewOutcome,
  SyncReviewStatus,
} from './syncReview';
import {
  AUTO_APPLY_GROUPS,
  countByStatus,
  isAutoApplicableDiff,
  isNotFoundError,
  MAX_PENDING_DIFFS,
  nextRequestDelay,
  walkOrder,
} from './syncReview';

const { Text } = Typography;

/** How many field lines the last-applied recap shows before eliding. */
const LAST_APPLIED_LINES = 6;

/** One glanceable value: nulls as a dash, everything long cut short. */
const compactValue = (value: unknown): string => {
  if (value == null) return '—';
  const text =
    typeof value === 'object' ? JSON.stringify(value) : String(value);
  return text.length > 48 ? `${text.slice(0, 48)}…` : text;
};

/** One fetched id awaiting the operator: a diff, or the error fetching it. */
interface PendingReview {
  id: number;
  /** Non-empty diff fields; null when the fetch failed. */
  fields: ScalarDiffFieldOut[] | null;
  error?: unknown;
}

/** The most recent successful apply, kept with the diff it wrote. */
interface LastApplied {
  id: number;
  fields: ScalarDiffFieldOut[];
  auto: boolean;
  /** Whether manually edited fields were overwritten too. */
  override: boolean;
}

interface SyncReviewWizardModalProps {
  /** User-catalog publication ids to walk through, in this order. */
  queue: number[];
  /** Publication titles by id, for the step header and the outcome list. */
  titles?: Record<number, string>;
  /**
   * Catalog scan: the queue is a blind id range, so an id with nothing
   * behind it is stepped over as `not_found` instead of stopping the walk,
   * the run can be stopped early with a summary of what it covered, the
   * result shows counts instead of a row per id.
   */
  scan?: boolean;
  /**
   * The modal is closing. `walked` says the operator saw the run through
   * (or stopped a scan) rather than cancelling out of it.
   */
  onClose: (walked: boolean) => void;
  /**
   * At least one refresh landed during the run; data should be refetched.
   * Fires once, as the modal closes — never per step.
   */
  onDone?: () => void;
}

/**
 * The sequential review, pipelined so the operator rarely waits: a
 * prefetcher walks the queue in the background — one request in flight,
 * request starts spaced by the rate budget — while the operator reviews.
 * A clean publication (or, in a scan, a missing id) resolves on its own;
 * a real diff joins a pending buffer the operator works through in queue
 * order, so the next diff is usually already loaded when Refresh or Skip
 * is clicked. Prefetching pauses at `MAX_PENDING_DIFFS` undecided diffs
 * and resumes as they are resolved. Mount it to start a run; it holds the
 * run's state, so remount for a fresh walk.
 *
 * The diff is fetched imperatively, not through a query hook: Refine's
 * query client keeps previous data as a placeholder across key changes,
 * which fed one step's empty diff to the next and walked whole runs on a
 * single request.
 *
 * List refetches wait until the modal closes: nothing behind the modal is
 * looked at mid-run, and every refetch fans out to each list query on the
 * page — the view chips alone are a query apiece — so a per-step refetch
 * would cost a run of a hundred applies a thousand requests.
 */
export const SyncReviewWizardModal = ({
  queue,
  titles,
  scan = false,
  onClose,
  onDone,
}: SyncReviewWizardModalProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const { open: notify } = useNotification();
  const invalidate = useInvalidate();
  const { mutateAsync } = useCustomMutation();

  // Next queue position the prefetcher will fetch.
  const [fetchIndex, setFetchIndex] = useState(0);
  // Fetched diffs (and fetch errors) awaiting a decision, in queue order.
  const [pending, setPending] = useState<PendingReview[]>([]);
  const [outcomes, setOutcomes] = useState<SyncReviewOutcome[]>([]);
  const [overrideManual, setOverrideManual] = useState(false);
  const [applying, setApplying] = useState(false);
  const [retrying, setRetrying] = useState(false);
  // The rate-limit gate: false right after a request settles, back to true
  // once the interval since that request's start has passed.
  const [ready, setReady] = useState(true);
  const [fetching, setFetching] = useState(false);
  // The operator ended a scan early; what was decided still gets a summary.
  const [stopped, setStopped] = useState(false);
  // A failed refresh on the head diff; skipping then records it as failed
  // rather than as a deliberate skip.
  const [applyError, setApplyError] = useState<string | null>(null);
  // Diffs confined to these field groups get applied without a stop.
  // Mirrored in a ref so an in-flight fetch routes its response by the
  // value at response time, not at request time.
  const [autoGroups, setAutoGroups] = useState<ReadonlySet<AutoApplyGroup>>(
    () => new Set()
  );
  const autoGroupsRef = useRef<ReadonlySet<AutoApplyGroup>>(autoGroups);
  // Qualifying diffs waiting for their automatic apply, in queue order.
  const [autoQueue, setAutoQueue] = useState<PendingReview[]>([]);
  const [autoApplying, setAutoApplying] = useState(false);
  // The most recent write with the diff it applied — the operator's running
  // proof that applies (especially automatic ones) do what was expected.
  const [lastApplied, setLastApplied] = useState<LastApplied | null>(null);
  // When the walk last started a request (diff or refresh), for the gate.
  const lastRequestAtRef = useRef(0);
  // Drops responses that land after the modal is gone.
  const aliveRef = useRef(true);
  useEffect(
    () => () => {
      aliveRef.current = false;
    },
    []
  );

  const tk = (suffix: string, options?: Record<string, unknown>) =>
    t(`user_catalog.publications.sync_review.${suffix}`, options);

  const fetchDone = fetchIndex >= queue.length;
  const finished =
    stopped || (fetchDone && pending.length === 0 && autoQueue.length === 0);
  // What the operator is looking at: always the earliest undecided id.
  const head = finished ? undefined : pending[0];

  // One request in flight, and automatic applies go before the next fetch
  // so the walk stays in queue order.
  const shouldFetch =
    !stopped &&
    !fetchDone &&
    ready &&
    !fetching &&
    !applying &&
    !retrying &&
    !autoApplying &&
    autoQueue.length === 0 &&
    pending.length < MAX_PENDING_DIFFS;
  const shouldAutoApply =
    !stopped &&
    ready &&
    !fetching &&
    !applying &&
    !retrying &&
    !autoApplying &&
    autoQueue.length > 0;

  // The prefetcher: one request in flight, run from here whenever the gate
  // is open and the pending buffer has room. Its response either resolves
  // the id on its own (clean, or missing in a scan) or appends a pending
  // review — the operator never waits for ids that need no decision.
  useEffect(() => {
    if (!shouldFetch) return;
    const id = queue[fetchIndex];
    if (id == null) return;
    lastRequestAtRef.current = Date.now();
    setFetching(true);
    axiosInstance
      .get<PublicationDiffOut>(`/user_catalog/publications/${id}/import-diff`)
      .then(({ data }) => {
        if (!aliveRef.current) return;
        const fields = data.fields ?? [];
        if (fields.length === 0) {
          setOutcomes((prev) => [
            ...prev,
            { publicationId: id, status: 'no_diff' },
          ]);
        } else if (isAutoApplicableDiff(fields, autoGroupsRef.current)) {
          setAutoQueue((prev) => [...prev, { id, fields }]);
        } else {
          setPending((prev) => [...prev, { id, fields }]);
        }
      })
      .catch((error: unknown) => {
        if (!aliveRef.current) return;
        if (scan && isNotFoundError(error)) {
          setOutcomes((prev) => [
            ...prev,
            { publicationId: id, status: 'not_found' },
          ]);
        } else {
          setPending((prev) => [...prev, { id, fields: null, error }]);
        }
      })
      .finally(() => {
        if (!aliveRef.current) return;
        setFetchIndex((i) => i + 1);
        setFetching(false);
        setReady(false);
      });
  }, [shouldFetch, fetchIndex, queue, scan]);

  // The automatic lane: a qualifying diff is applied without a stop, one
  // POST at a time through the same gate. `override` stays false — a
  // manually edited field never reaches this lane, and if one ever did the
  // apply would leave it alone. A failed automatic apply falls back to the
  // manual buffer with its diff intact.
  useEffect(() => {
    if (!shouldAutoApply) return;
    const item = autoQueue[0];
    if (item == null) return;
    lastRequestAtRef.current = Date.now();
    setAutoApplying(true);
    mutateAsync({
      url: `user_catalog/publications/${item.id}/import-apply`,
      method: 'post',
      values: { override: false },
      successNotification: false,
      errorNotification: false,
    })
      .then(() => {
        if (!aliveRef.current) return;
        setOutcomes((prev) => [
          ...prev,
          { publicationId: item.id, status: 'auto_refreshed' },
        ]);
        setLastApplied({
          id: item.id,
          fields: item.fields ?? [],
          auto: true,
          override: false,
        });
      })
      .catch(() => {
        if (!aliveRef.current) return;
        setPending((prev) => [...prev, { id: item.id, fields: item.fields }]);
      })
      .finally(() => {
        if (!aliveRef.current) return;
        setAutoQueue((prev) => prev.slice(1));
        setAutoApplying(false);
        setReady(false);
      });
  }, [shouldAutoApply, autoQueue, mutateAsync]);

  /**
   * Toggle one group of the automatic lane. Enabling a group also drains
   * diffs already buffered for review that now qualify — including the
   * one on screen. Disabling one hands every queued diff that no longer
   * qualifies back to the manual buffer: only the apply already in flight
   * completes. Appending is fine — `walkOrder` restores the result list's
   * order.
   */
  const toggleAutoGroup = (group: AutoApplyGroup, checked: boolean) => {
    const next = new Set(autoGroups);
    if (checked) next.add(group);
    else next.delete(group);
    autoGroupsRef.current = next;
    setAutoGroups(next);

    const queued = autoApplying ? autoQueue.slice(1) : autoQueue;
    const returning = queued.filter(
      (item) => !isAutoApplicableDiff(item.fields ?? [], next)
    );
    if (returning.length > 0) {
      setPending((prev) => [...prev, ...returning]);
      // Functional on purpose: a fetch response may append to the auto
      // queue in this same frame, and a just-finished apply removes its own
      // head — a plain replace would clobber either.
      setAutoQueue((prev) => prev.filter((item) => !returning.includes(item)));
    }

    const movable = pending.filter(
      (item) => item.fields != null && isAutoApplicableDiff(item.fields, next)
    );
    if (movable.length === 0) return;
    // Moving the on-screen head resets what belonged to it, exactly as
    // resolving it would — a stale apply error must not travel to the
    // next head.
    if (movable[0] === pending[0]) {
      setApplyError(null);
      setOverrideManual(false);
    }
    setAutoQueue((prev) => [...prev, ...movable]);
    setPending((prev) => prev.filter((item) => !movable.includes(item)));
  };

  // Re-open the gate once the interval since the last request's start has
  // passed — the request's own duration spends the budget, so after a slow
  // response the next fetch fires immediately.
  useEffect(() => {
    if (ready) return;
    const timer = setTimeout(
      () => setReady(true),
      nextRequestDelay(lastRequestAtRef.current, Date.now())
    );
    return () => clearTimeout(timer);
  }, [ready]);

  /** The operator decided the head diff; hand the slot to the next one. */
  const resolveHead = (status: SyncReviewStatus, error?: string) => {
    if (!head) return;
    setOutcomes((prev) => [
      ...prev,
      { publicationId: head.id, status, ...(error ? { error } : {}) },
    ]);
    setPending((prev) => prev.slice(1));
    setOverrideManual(false);
    setApplyError(null);
  };

  const close = () => {
    if (applying) return;
    // The one refetch of the run, and only when something was written. An
    // automatic apply still in flight counts: its success handler dies with
    // the modal, but the write can land anyway.
    if (
      autoApplying ||
      countByStatus(outcomes, 'refreshed') +
        countByStatus(outcomes, 'auto_refreshed') >
        0
    ) {
      invalidate({
        resource: 'user_catalog.publications',
        invalidates: ['list'],
      });
      onDone?.();
    }
    onClose(finished && outcomes.length > 0);
  };

  const refresh = async () => {
    if (!head) return;
    setApplying(true);
    lastRequestAtRef.current = Date.now();
    try {
      await mutateAsync({
        url: `user_catalog/publications/${head.id}/import-apply`,
        method: 'post',
        values: { override: overrideManual },
        successNotification: false,
        errorNotification: false,
      });
      setLastApplied({
        id: head.id,
        fields: head.fields ?? [],
        auto: false,
        override: overrideManual,
      });
      resolveHead('refreshed');
    } catch (error) {
      const message = extractBackendMessage(error) ?? tk('apply_error');
      setApplyError(message);
      notify?.({
        type: 'error',
        message: tk('title'),
        description: message,
      });
    } finally {
      setApplying(false);
      // The apply spent rate budget too: close the gate so the next
      // prefetch start is spaced from the POST, not fired right after it.
      setReady(false);
    }
  };

  const skip = () => {
    if (!head) return;
    if (head.error != null) {
      resolveHead(
        'failed',
        extractBackendMessage(head.error) ?? tk('load_error')
      );
    } else if (applyError) {
      resolveHead('failed', applyError);
    } else {
      resolveHead('skipped');
    }
  };

  /** Re-fetch the errored head in place; the prefetcher has moved on. */
  const retry = async () => {
    if (!head) return;
    const id = head.id;
    setRetrying(true);
    lastRequestAtRef.current = Date.now();
    try {
      const { data } = await axiosInstance.get<PublicationDiffOut>(
        `/user_catalog/publications/${id}/import-diff`
      );
      if (!aliveRef.current) return;
      const fields = data.fields ?? [];
      if (fields.length === 0) {
        resolveHead('no_diff');
      } else {
        setPending(([, ...rest]) => [{ id, fields }, ...rest]);
      }
    } catch (error) {
      if (!aliveRef.current) return;
      setPending(([, ...rest]) => [{ id, fields: null, error }, ...rest]);
    } finally {
      if (aliveRef.current) {
        setRetrying(false);
        // The retry spent rate budget too: space the next prefetch start
        // from it.
        setReady(false);
      }
    }
  };

  // A scan's 404 never reaches the buffer; an error item here is one the
  // operator has to decide on.
  const headError = head != null && head.error != null;
  const hasDiff = head != null && head.fields != null;
  // Nothing to review yet: the prefetcher is still working the queue down.
  const checking = !finished && head == null;

  // Keyboard review: Enter = the primary action (refresh, or retry on an
  // errored head), ArrowRight = skip. Mirrors the buttons' disabled logic.
  // A focused button keeps its own Enter behaviour; text-entry fields are
  // left alone (the wizard has none, but the guard is cheap insurance).
  useEffect(() => {
    if (finished) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (
        tag === 'INPUT' &&
        !['checkbox', 'radio'].includes((target as HTMLInputElement).type)
      )
        return;
      if (event.key === 'Enter') {
        if (tag === 'BUTTON') return;
        event.preventDefault();
        if (headError) {
          if (!retrying) void retry();
        } else if (hasDiff && !applying && !retrying) {
          void refresh();
        }
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        if (head != null && !applying && !retrying) skip();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  return (
    <Modal
      open
      title={tk('title')}
      closable={!applying}
      keyboard={!applying}
      maskClosable={false}
      onCancel={close}
      destroyOnClose
      width={900}
      footer={
        <Flex align="center" gap={token.marginSM}>
          <Text
            type="secondary"
            style={{ flex: 1, minWidth: 0, textAlign: 'left' }}
          >
            {!finished &&
              tk('progress', {
                checked: fetchIndex,
                total: queue.length,
                pending: pending.length,
              })}
          </Text>
          {finished ? (
            <Button type="primary" onClick={close}>
              {t('actions.close')}
            </Button>
          ) : (
            <>
              <Button onClick={close} disabled={applying}>
                {t('actions.cancel')}
              </Button>
              {scan && (
                <Button onClick={() => setStopped(true)} disabled={applying}>
                  {tk('stop')}
                </Button>
              )}
              <Button
                onClick={skip}
                disabled={head == null || applying || retrying}
              >
                {tk('skip')}
              </Button>
              {headError ? (
                <Button type="primary" loading={retrying} onClick={retry}>
                  {tk('retry')}
                </Button>
              ) : (
                <Button
                  type="primary"
                  loading={applying}
                  disabled={!hasDiff}
                  onClick={refresh}
                >
                  {applying ? tk('refreshing') : tk('refresh')}
                </Button>
              )}
            </>
          )}
        </Flex>
      }
    >
      {finished ? (
        <SyncReviewResult
          outcomes={scan ? outcomes : walkOrder(outcomes, queue)}
          titles={titles}
          countsOnly={scan}
        />
      ) : (
        <Flex vertical gap={token.marginSM}>
          <Flex vertical gap={token.marginXXS}>
            {AUTO_APPLY_GROUPS.map((group) => (
              <Checkbox
                key={group}
                checked={autoGroups.has(group)}
                disabled={applying || retrying}
                onChange={(e) => toggleAutoGroup(group, e.target.checked)}
              >
                {tk(`auto_apply.${group}`)}
              </Checkbox>
            ))}
          </Flex>
          {lastApplied != null &&
            (() => {
              // Only what the apply actually wrote: a locked field is never
              // written, and without the override neither is a manual edit.
              const written = lastApplied.fields.filter((field) =>
                willImportApply(field, lastApplied.override)
              );
              const shown = written.slice(0, LAST_APPLIED_LINES);
              return (
                <Flex
                  vertical
                  gap={token.marginXXS}
                  style={{
                    background: token.colorFillQuaternary,
                    borderRadius: token.borderRadiusLG,
                    padding: `${token.paddingXS}px ${token.paddingSM}px`,
                  }}
                >
                  <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                    {tk(
                      lastApplied.auto ? 'last_applied_auto' : 'last_applied'
                    )}
                    {': '}
                    <ShowLink
                      resource="user_catalog.publications"
                      id={lastApplied.id}
                      newTab
                    >
                      {titles?.[lastApplied.id] ?? `#${lastApplied.id}`} (#
                      {lastApplied.id})
                    </ShowLink>
                  </Text>
                  {shown.map((field) => (
                    <Text
                      key={field.path}
                      type="secondary"
                      style={{
                        fontSize: token.fontSizeSM,
                        paddingLeft: token.paddingSM,
                      }}
                    >
                      {diffFieldLabel(t, field.path)}:{' '}
                      {compactValue(field.local)} →{' '}
                      {compactValue(field.upstream)}
                    </Text>
                  ))}
                  {written.length > shown.length && (
                    <Text
                      type="secondary"
                      style={{
                        fontSize: token.fontSizeSM,
                        paddingLeft: token.paddingSM,
                      }}
                    >
                      {tk('last_applied_more', {
                        count: written.length - shown.length,
                      })}
                    </Text>
                  )}
                </Flex>
              );
            })()}
          {head != null && (
            <Flex gap={token.marginXS} align="baseline" wrap>
              <Text strong>{titles?.[head.id] ?? `#${head.id}`}</Text>
              <Text type="secondary">#{head.id}</Text>
            </Flex>
          )}
          {headError ? (
            <Text type="danger">
              {extractBackendMessage(head?.error) ?? tk('load_error')}
            </Text>
          ) : checking ? (
            <Flex align="center" gap={token.marginSM}>
              <Spin />
              <Text type="secondary">{tk('checking')}</Text>
            </Flex>
          ) : hasDiff ? (
            <ImportDiffTable
              fields={head?.fields ?? []}
              overrideManual={overrideManual}
              onOverrideManualChange={setOverrideManual}
            />
          ) : null}
        </Flex>
      )}
    </Modal>
  );
};
